/**
 * メッセージテンプレートの読み取り機能を提供するモジュール
 * @date 2025/06/25
 */
import useSWR from 'swr';
import {
  MessageTemplate,
  MessageTemplateFormType,
  MessageTemplateId,
  toFormTypeFromMessageTemplate,
} from 'src/models/messageTemplate';
import { useMessageTemplateRepository } from 'src/repositories/messageTemplate/repository';
import { messageTemplateCacheKeyGenerator } from './cache';
import { message } from 'antd';
import {
  LoginAccountState,
  useLoginAccount,
} from 'src/globalStates/loginAccount';

/**
 * メッセージテンプレート一覧取得用のクエリ型
 */
export type MessageTemplateGetListQuery = { account: LoginAccountState };

/**
 * メッセージテンプレート一覧取得のレスポンス型
 */
type MessageTemplateGetListResponse = { messageTemplates: MessageTemplate[] };

/**
 * メッセージテンプレート一覧を取得するためのカスタムフック
 * @returns SWRレスポンスオブジェクト
 */
export const useMessageTemplateList = () => {
  const repository = useMessageTemplateRepository();
  const account = useLoginAccount();

  return useSWR<MessageTemplateGetListResponse>(
    messageTemplateCacheKeyGenerator.generateListKey({
      query: { account },
      ignore: account.merchantId === undefined,
    }),
    async () => {
      const _getValue = await repository.getList({ account });
      return {
        messageTemplates: _getValue,
      };
    },
    {
      onError: (_) => {
        message.error(
          'テンプレートリストの取得時にエラーが発生しました。更新して再度お試しください。'
        );
      },
    }
  );
};

/**
 * 個別のメッセージテンプレート取得用のクエリ型
 */
export type MessageTemplateGetItemQuery = {
  messageTemplateId: MessageTemplateId;
  account: LoginAccountState;
};

/**
 * 個別のメッセージテンプレート取得のレスポンス型
 */
export type MessageTemplateGetItemResponse = { messageTemplate: MessageTemplateFormType };

/**
 * 個別のメッセージテンプレートを取得するためのカスタムフック
 * @param templateId - 取得対象のテンプレートID
 * @returns SWRレスポンスオブジェクト
 */
export const useMessageTemplateFormItem = ({ mtId }: { mtId: MessageTemplateId }) => {
  const repository = useMessageTemplateRepository();
  const account = useLoginAccount();

  return useSWR<MessageTemplateGetItemResponse>(
    messageTemplateCacheKeyGenerator.generateItemKey({
      query: { messageTemplateId: mtId },
      ignore: !account.merchantId,
    }),
    async () => {
      const _getValue = await repository.getDoc({ messageTemplateId: mtId, account });
      return {
        messageTemplate: toFormTypeFromMessageTemplate(_getValue),
      };
    },
    {
      onError: (_) => {
        message.error(
          'テンプレートデータの取得時にエラーが発生しました。更新して再度お試しください。'
        );
      },
    }
  );
};